/**
 * AsfUniversalCanvasObjectCtrl is a controller class for managing Universal Canvas objects.
 * It extends the BaseObjectDirective and integrates various AngularJS services and utilities.
 */
import BaseObjectDirective from './BaseObjectDirective';

export class AsfUniversalCanvasObjectCtrl extends BaseObjectDirective {
    /**
     * Constructor for AsfUniversalCanvasObjectCtrl.
     * @param {Object} $scope - AngularJS scope object.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} $uibModal - AngularUI Bootstrap modal service.
     * @param {Object} $rootScope - AngularJS root scope object.
     * @param {Object} $routeParams - AngularJS route parameters.
     * @param {Object} TaskDataService - Service for task data operations.
     * @param {Object} TaskInfoService - Service for task information.
     * @param {Object} TaskSelectionService - Service for task selection.
     * @param {Object} ObjectInstanceRegistrationService - Service for object instance registration.
     * @param {Object} $signals - Signal service for event handling.
     * @param {Object} APIService - API service for backend communication.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} $interval - AngularJS interval service.
     * @param {Object} $bridges - Bridge service for inter-component communication.
     * @param {Object} $sce - AngularJS SCE service for secure context escaping.
     * @param {Object} $filter - AngularJS filter service.
     * @param {Object} $window - AngularJS window object.
     * @param {Object} $ - jQuery instance.
     * @param {Object} BridgeRuntimeService - Service for bridge runtime operations.
     * @param {Object} FreezeService - Service for freezing UI elements.
     * @param {Object} UniversalCanvasService - Service for Universal Canvas operations.
     * @param {Object} SheetLoadedService - Service for handling sheet loading.
     */
    constructor($scope, $injector, $uibModal, $rootScope, $routeParams, TaskDataService, TaskInfoService, TaskSelectionService, ObjectInstanceRegistrationService, $signals, APIService, $timeout, $interval, $bridges, $sce, $filter, $window, $, BridgeRuntimeService, FreezeService, UniversalCanvasService, SheetLoadedService) {
        super($injector, $scope);
        this.$scope = $scope;
        this.$injector = $injector;
        this.$uibModal = $uibModal;
        this.$rootScope = $rootScope;
        this.$routeParams = $routeParams;
        this.TaskDataService = TaskDataService;
        this.TaskInfoService = TaskInfoService;
        this.TaskSelectionService = TaskSelectionService;
        this.ObjectInstanceRegistrationService = ObjectInstanceRegistrationService;
        this.$signals = $signals;
        this.APIService = APIService;
        this.$timeout = $timeout;
        this.$interval = $interval;
        this.$bridges = $bridges;
        this.$sce = $sce;
        this.$filter = $filter;
        this.$window = $window;
        this.$ = $;
        this.BridgeRuntimeService = BridgeRuntimeService;
        this.FreezeService = FreezeService;
        this.UniversalCanvasService = UniversalCanvasService;
        this.SheetLoadedService = SheetLoadedService;

        this.taskContext = TaskDataService.fetchContext();
        this.defaultPaletteName = 'please enter a name for this palette';
        this.changedMedia = false;
        this.isPreview = $rootScope.isPreview;

        this.isCMSNotPreview = $rootScope.isCMS && !$rootScope.isPreview;
        $scope.isCMS = this.isCMSNotPreview;
        $scope.isPreview = $rootScope.isPreview;
        $scope.prefsMode = false;
        $scope.hasBrowseImage = false; //show media manager button
        $scope.hasUniversalCanvasPalette = true;
        $scope.hasUniversalCanvasBackgroundImages = true;
        $scope.taskuuid = $routeParams.taskUUID;
        if (!angular.isDefined($scope.taskuuid)) {
            $scope.taskuuid = TaskSelectionService.getTaskUUID();
        }
        $scope.objectUUID = $scope.contentObject.getUUID();
        $scope.universalCanvasURLDefault = "/iframe-tools/universal-canvas/index.html?uuid=" + $scope.objectUUID;
        $scope.universalCanvasURL = $scope.universalCanvasURLDefault;

        this.compressionActivate = true;
        this.compressionKeyword = "--COMPRESSION--";
        this.compressionOptions = {
            to: 'string',
            level: 9,
            gzip: true,
            header: {
                text: true
            }
        };

        this.availableTools = [
            { id: 'tool_fhpath', name: 'Pencil Tool' },
            { id: 'tool_line', name: 'Line Tool' },
            { id: "tool_path", name: "Path Tool" },
            { id: 'tool_rect', name: 'Rect Tool' },
            { id: 'tool_ellipse', name: 'Ellipse Tool' },
            { id: 'tool_circle', name: 'Circle Tool' },
            { id: 'tool_rect_radius', name: 'Rect Radius Tool' },
            { id: 'tool_diamond', name: 'Diamond Tool' },
            { id: 'tool_text', name: 'Text Tool' },
            { id: 'tool_arrow', name: 'Arrow Tool' },
            // { id: 'tool_shapelib', name: 'Palettes' }
        ];

        this.opacityMap = {
            '100%': 1.0,
            '90%': 0.9,
            '80%': 0.8,
            '70%': 0.7,
            '60%': 0.6,
            '50%': 0.5,
            '40%': 0.4,
            '30%': 0.3,
            '20%': 0.2,
            '10%': 0.1,
            '0%': 0.0
        };
        this.opacity = ['100%', '90%', '80%', '70%', '60%', '50%', '40%', '30%', '20%', '10%', '0%'];

        // Initialize pre-attachment for this instance
        let attachement = angular.copy(this.$scope.contentObject.getAttachement());
        attachement = this.decompression(attachement);
        attachement = !attachement ? {} : attachement;
        this.preAttachement = angular.copy(this.fixSVGMediaPaths(attachement));

        this.modalInstance = null;

        this.init();
    }

    /**
     * Compresses the attachment if compression is activated.
     * @param {Object} attachement - The attachment to be compressed.
     * @returns {Object} - The compressed attachment.
     */
    compression(attachement) {
        return this.UniversalCanvasService.compression(attachement);
    }

    /**
     * Decompresses the attachment if compression is activated.
     * @param {Object} attachement - The attachment to be decompressed.
     * @returns {Object} - The decompressed attachment.
     */
    decompression(attachement) {
        return this.UniversalCanvasService.decompression(attachement);
    }

    /**
     * Fixes the media paths in the SVG data of the attachment.
     * @param {Object} attachement - The attachment containing SVG data.
     * @returns {Object} - The attachment with fixed SVG media paths.
     */
    fixSVGMediaPaths(attachement) {
        return this.UniversalCanvasService.fixSVGMediaPaths(attachement, {
            taskuuid: this.$scope.taskuuid,
            isCMSNotPreview: this.isCMSNotPreview,
            isPDFMode: this.$scope.isPDFMode,
            examExport: this.$scope.examExport
        });
    }

    /**
     * Creates an empty SVG placeholder for PDF.
     * @returns {String} - The SVG string for the placeholder.
     */
    createEmptyPDFPlaceHolderSVG() {
        var w = this.$scope.$parent.$parent.size == 50 ? 450 : 919,
            h = parseInt(this.$scope.contentObject.getPreferences().canvasHeight),
            svgHead =
                '<svg height="' + h + '" id="svgroot" overflow="visible" width="' + w + '" x="' + w + '" xlinkns="http://www.w3.org/1999/xlink" xmlns="http://www.w3.org/2000/svg" y="' + h + '">' +
                '<svg height="' + h + '" id="canvasBackground" overflow="none" style="pointer-events:none" width="' + w + '" x="0" y="0">' +
                '<rect fill="#cacaca" height="100%" stroke="#000" style="pointer-events:none" width="100%" x="0" y="0"/>',
            svgFoot = '</svg></svg>';

        var bgURL = false,
            bgContent = this.$scope.contentObject.getPreferences().mediaContent;
        if (typeof bgContent != 'undefined' && bgContent) {
            bgURL = this.taskContext.read(bgContent).getExtendedMediaURL(this.$scope.taskuuid);
            svgBg = '<image height="100%" id="background_image" preserveAspectRatio="xMinYMin" style="pointer-events:none" width="100%" xlink:href="' +
                bgURL +
                '" xmlns:xlink="http://www.w3.org/1999/xlink"/>';

            return svgHead + svgBg + svgFoot;
        }

        return svgHead + svgFoot;
    }

    /**
     * Sets the style for the iframe containing the canvas.
     */
    iframestyle() {
        if (!this.$scope.contentObject.getPreferences().canvasHeight || parseInt(this.$scope.contentObject.getPreferences().canvasHeight) < 100) {
            this.$scope.contentObject.setPreference('canvasHeight', 100);
        }

        var iFrameHeight = 0,
            canvasHeightTmp = parseInt(this.$scope.contentObject.getPreferences().canvasHeight),
            enabledToolsQty = this.$scope.contentObject.getPreferences().enabledTools.length,
            enabledToolsQty = this.$scope.contentObject.getPreferences().palettes.length > 0 ? enabledToolsQty + 1 : enabledToolsQty, // considering palettes if at least one exists
            containerWidth = this.$scope.$parent.$parent.size,     // 100 or 50 for two columns
            menuBarHeight = 43,
            contextPanelHeight = 77,
            menuBarRowsQty = 1,      // 6 elements fits in one bar
            contextPanelRowsQty = 1;      // in 1-column layout all elements fit in one row

        canvasHeightTmp = canvasHeightTmp < 100 ? 100 : canvasHeightTmp;

        if (enabledToolsQty > 6 && containerWidth == 50) {
            menuBarRowsQty = 2;
        }

        if (containerWidth == 50) {
            contextPanelRowsQty = 2;
        }

        if (!this.$scope.isCMS || this.isPreview) {
            if (this.$scope.contentObject.getPreferences().hideMenuBar) {
                menuBarHeight = 0;
            }

            if (containerWidth == 50) {
                contextPanelRowsQty = 1;
            }
        }

        iFrameHeight = (menuBarHeight * menuBarRowsQty) + canvasHeightTmp + (contextPanelHeight * contextPanelRowsQty);

        this.$scope.iFrameStyle = {
            'width': containerWidth == 50 ? '450px' : '919px',
            'height': (iFrameHeight) + 'px',
            'border': '0'
        };
    }

    /**
     * Retrieves the available palettes for the canvas.
     * @returns {Array} - List of available palettes.
     */
    availablePalettes() {
        var instances = _.filter(this.taskContext.findInstancesByType('universalCanvasPalette'), function (item) {
            return item.getName().indexOf('__IB-BACKGROUND-IMAGE-DEFAULT__') === -1 && item.getName().indexOf('__IB-BACKGROUND-IMAGES__') === -1
        });

        return Object.keys(instances).map(function (el) {
            return {
                id: instances[el].getUUID(),
                name: instances[el].getName()
            }
        });
    }

    /**
     * Reloads the canvas by destroying the history object and updating the iframe style and URL.
     */
    reloadCanvas() {
        this.UniversalCanvasService.destroyHistoryObject(this.$scope.objectUUID);
        this.iframestyle();
        var _nocache_please = '&r=' + btoa(Math.floor((Math.random() * 1000000000001)));
        this.$scope.universalCanvasURL = this.$scope.universalCanvasURLDefault + _nocache_please;
    }

    /**
     * Initializes the preferences for the canvas.
     */
    initPreferences() {
        var canvasHeight = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().canvasHeight : undefined);
        var showGrid = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().showGrid : undefined);
        var showColorOptions = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().showColorOptions : undefined);
        var showSideBarPalettes = ((typeof this.$scope.contentObject.getPreferences() != "undefined" && angular.isDefined(this.$scope.contentObject.getPreferences().showSideBarPalettes)) ? this.$scope.contentObject.getPreferences().showSideBarPalettes : false);
        var gridSnapping = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().gridSnapping : undefined);
        var initFill = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().initFill : undefined);
        var initStroke = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().initStroke : undefined);
        var showStrokeOption = (typeof this.$scope.contentObject.getPreferences() != "undefined" && typeof this.$scope.contentObject.getPreferences().showStrokeOption != "undefined" ? this.$scope.contentObject.getPreferences().showStrokeOption : false);
        var gridMainLineAlpha = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().gridMainLineAlpha : undefined);
        var gridMainLineColor = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().gridMainLineColor : undefined);
        var gridSubLineAlpha = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().gridSubLineAlpha : undefined);
        var gridSubLineColor = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().gridSubLineColor : undefined);
        var gridMainRectDimension = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().gridMainRectDimension : undefined);
        var gridSubRectNumber = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().gridSubRectNumber : undefined);
        var enabledTools = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().enabledTools : undefined);
        var palettes = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().palettes : undefined);
        var selectBackgroundColor = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().selectBackgroundColor : undefined);
        var mediaContent = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().mediaContent : undefined);
        var includedInPDF = (typeof this.$scope.contentObject.getPreferences() !== "undefined" ? this.$scope.contentObject.getPreferences().includedInPDF : false);
        var hideMenuBar = (typeof this.$scope.contentObject.getPreferences() !== "undefined" ? this.$scope.contentObject.getPreferences().hideMenuBar : false);
        var disableContextMenu = (typeof this.$scope.contentObject.getPreferences() !== "undefined" ? this.$scope.contentObject.getPreferences().disableContextMenu : false); /*CONTINUED*/

        this.$scope.$watch('preferencesValues', function (val1, val2) {
            if (val1 == val2) return;

            var preferences = {};
            angular.forEach(this.$scope.preferencesValues, function (value, key) {
                preferences[key] = value;
                if (key == 'canvasHeight' && val1.canvasHeight != val2.canvasHeight) {
                    this.iframestyle();
                }
            }.bind(this));
            this.$scope.contentObject.setPreferences(preferences);
            if (val1.includedInPDF !== val2.includedInPDF) {
                return false;
            }
            this.reloadCanvas();
        }.bind(this), true);

        this.$scope.$watch(
            function () {
                return Object.keys(this.taskContext.findInstancesByType('universalCanvasPalette')).length;
            }.bind(this),
            function (newVal, oldVal) {
                if (newVal == oldVal) return;
                this.$signals.emit("rendering_rows", null);
            }.bind(this)
        );

        this.$scope.preferencesValues = {};
        this.$scope.preferencesValues['canvasHeight'] = canvasHeight;
        this.$scope.preferencesValues['showGrid'] = showGrid;
        this.$scope.preferencesValues['showColorOptions'] = showColorOptions;
        this.$scope.preferencesValues['showSideBarPalettes'] = showSideBarPalettes;
        this.$scope.preferencesValues['gridSnapping'] = gridSnapping;
        this.$scope.preferencesValues['initFill'] = initFill;
        this.$scope.preferencesValues['initStroke'] = initStroke;
        this.$scope.preferencesValues['showStrokeOption'] = showStrokeOption;
        this.$scope.preferencesValues['gridMainLineAlpha'] = gridMainLineAlpha;
        this.$scope.preferencesValues['gridMainLineColor'] = gridMainLineColor;
        this.$scope.preferencesValues['gridSubLineAlpha'] = gridSubLineAlpha;
        this.$scope.preferencesValues['gridSubLineColor'] = gridSubLineColor;
        this.$scope.preferencesValues['gridMainRectDimension'] = gridMainRectDimension;
        this.$scope.preferencesValues['gridSubRectNumber'] = gridSubRectNumber;
        this.$scope.preferencesValues['enabledTools'] = enabledTools;
        this.$scope.preferencesValues['palettes'] = palettes;
        this.$scope.preferencesValues['selectBackgroundColor'] = selectBackgroundColor;
        this.$scope.preferencesValues['mediaContent'] = mediaContent;
        this.$scope.preferencesValues['includedInPDF'] = typeof includedInPDF !== 'undefined' ? includedInPDF : false;
        this.$scope.preferencesValues['hideMenuBar'] = typeof hideMenuBar !== 'undefined' ? hideMenuBar : false;
        this.$scope.preferencesValues['disableContextMenu'] = typeof disableContextMenu !== 'undefined' ? disableContextMenu : false;

        this.previous_preferences = this.$scope.preferencesValues;

        this.$scope.preferences = [
            {
                '00-enabledTools': [
                    {
                        'type': 'multiselect',
                        'name': 'Choose Tools:',
                        'tools': this.availableTools,
                        'valueKey': 'enabledTools'
                    }
                ],
                '01-palettes': [
                    {
                        'type': 'multiselect',
                        'name': 'Choose Palettes:',
                        'tools': this.availablePalettes(),
                        'valueKey': 'palettes'
                    }
                ],
                '17-showSideBarPalettes': [
                    { 'type': 'checkbox', 'name': 'Show Side-Bar Palettes:', 'valueKey': 'showSideBarPalettes' }
                ],
                '18-background-color': [
                    { 'type': 'checkbox', 'name': 'Select Background Color', 'valueKey': 'selectBackgroundColor' },
                ],
                '19-showStrokeOption': [
                    { 'type': 'checkbox', 'name': 'Show Stroke Option for candidates:', 'valueKey': 'showStrokeOption' }
                ],
                '02-showGrid': [
                    { 'type': 'checkbox', 'name': 'Show Grid', 'valueKey': 'showGrid' },
                ],
                '04-gridSnapping': [
                    { 'type': 'checkbox', 'name': 'Snap to Grid', 'valueKey': 'gridSnapping' },
                ],
                '06-gridMainRectDimension': [
                    { 'type': 'input', 'name': 'Main Rect Dimension:', 'valueKey': 'gridMainRectDimension' }
                ],
                '08-gridSubRectNumber': [
                    { 'type': 'input', 'name': 'Sub Rect Number:', 'valueKey': 'gridSubRectNumber' }
                ],
                '10-gridMainLineAlpha': [
                    {
                        'type': 'select',
                        'name': 'Select grid main line opacity:',
                        'options': this.opacity,
                        'valueKey': 'gridMainLineAlpha'
                    }
                ],
                '12-gridSubLineAlpha': [
                    {
                        'type': 'select',
                        'name': 'Select grid sub line opacity:',
                        'options': this.opacity,
                        'valueKey': 'gridSubLineAlpha'
                    }
                ],
                '14-includedInPDF': [
                    { 'type': 'checkbox', 'name': 'Included in Response PDF', 'valueKey': 'includedInPDF' }
                ],
                '16-disableContextMenu': [
                    {
                        'type': 'checkbox',
                        'name': 'Disable Context Menu for candidates',
                        'valueKey': 'disableContextMenu'
                    }
                ],
                '03-canvasHeight': [
                    { 'type': 'input', 'name': 'Canvas Height:', 'valueKey': 'canvasHeight' }
                ],
                '05-initFill': [
                    { 'type': 'colorpicker', 'name': 'Select fill color:', 'valueKey': 'initFill' }
                ],
                '07-initStroke': [
                    { 'type': 'colorpicker', 'name': 'Select stroke color:', 'valueKey': 'initStroke' }
                ],
                '09-gridMainLineColor': [
                    { 'type': 'colorpicker', 'name': 'Select grid main line color:', 'valueKey': 'gridMainLineColor' }
                ],
                '11-gridSubLineColor': [
                    { 'type': 'colorpicker', 'name': 'Select grid sub line color:', 'valueKey': 'gridSubLineColor' }
                ],
                '13-showColorOptions': [
                    { 'type': 'checkbox', 'name': 'Show colorpicker:', 'valueKey': 'showColorOptions' }
                ],
                '15-hideMenuBar': [
                    { 'type': 'checkbox', 'name': 'Hide Menu Bar for candidates', 'valueKey': 'hideMenuBar' }
                ]
            },
        ];
    }

    isLanguageAvailable(language) {
        for (var lang in this.$window.languages) {
            if (lang.match('^' + language + '_')) {
                return true;
            }
        }
        return false;
    }

    initOldBackgroundImageContent() {
        var attachement = this.getAttachement();
        var oldMediaContent;
        if (this.$scope.contentObject.getPreferences().mediaContent) {
            oldMediaContent = this.$scope.contentObject.getPreferences().mediaContent;
            this.$scope.contentObject.getPreferences().mediaContent = null;
        } else if (this.$scope.contentObject.data &&
            this.$scope.contentObject.data.entity_content &&
            this.$scope.contentObject.data.entity_content.preferences &&
            this.$scope.contentObject.data.entity_content.preferences.mediaContent) {
            oldMediaContent = this.$scope.contentObject.data.entity_content.preferences.mediaContent;
            this.$scope.contentObject.data.entity_content.preferences.mediaContent = null;
        }

        if (!attachement.backgroundImage && oldMediaContent) {
            attachement.backgroundImage = oldMediaContent;
            var instances = this.taskContext.findInstancesByType('universalCanvasPalette');
            var defaultImagePalettes = _.filter(instances, function (item) {
                return item.getName() === ('__IB-BACKGROUND-IMAGE-DEFAULT__' + this.$scope.contentObject.getUUID());
            }.bind(this));
            if (defaultImagePalettes &&
                defaultImagePalettes[0]) {
                defaultImagePalettes[0].setItems([{
                    "position": 0,
                    "uuid": attachement.backgroundImage
                }]);
            }

            this.$scope.contentObject.setAttachement(this.compression(attachement));
        }
    }

    cleanSVGForPDF(orgSVG) {
        return this.UniversalCanvasService.cleanSVGForPDF(orgSVG, this.$);
    }

    /**
     * Replaces old font family names with new Universal Canvas font family names in SVG content.
     * @param {string} svg - The SVG content to process.
     * @returns {string} The SVG content with updated font family names.
     */
    addUCFontsToSVG(svg) {
        return this.UniversalCanvasService.addUCFontsToSVG(svg);
    }

    /**
     * Handles media selection events.
     * @param {Object} selectedMedia - The selected media object.
     */
    onSelectMedia = (selectedMedia) => {
        if (selectedMedia.contentobjectuuid == this.$scope.contentObject.getUUID()) {
            if (selectedMedia) {
                this.$scope.preferencesValues['mediaContent'] = selectedMedia.uuid;
            } else {
                this.$scope.preferencesValues['mediaContent'] = '';
            }
            this.changedMedia = true;

            if (this.modalInstance !== null) {
                this.modal.close();
            }
        }
    }

    /**
     * Handles sheet blur events.
     */
    onBeforeSheetBlur = () => {
        if (!this.isCMSNotPreview) {
            this.$scope.universalcanvasObject = this.ObjectInstanceRegistrationService.getObjectByUUID(this.$scope.objectUUID);
            if (typeof (this.$scope.universalcanvasObject) == 'undefined') {
                console.warn("UC: Skipping onBeforeSheetBlur.fireSave() - uc object not registered");
                return;
            }
            if (!angular.isFunction(this.$scope.universalcanvasObject.fireSave)) {
                console.warn("UC: Skipping onBeforeSheetBlur.fireSave() - now fireSave method found");
                return;
            }
            if (this.advanceQuestionStatusIfContentChanged()) {
                this.$scope.universalcanvasObject.fireSave();
            }
        }
    }

    init() {
        this.taskContext = this.TaskDataService.fetchContext();
        this.defaultPaletteName = 'please enter a name for this palette';
        this.changedMedia = false;
        this.isPreview = this.$rootScope.isPreview;

        var isCMSNotPreview = this.$rootScope.isCMS && !this.$rootScope.isPreview;
        this.$scope.isCMS = isCMS;
        this.$scope.isPreview = this.$rootScope.isPreview;
        this.$scope.prefsMode = false;
        this.$scope.hasBrowseImage = false;
        this.$scope.hasUniversalCanvasPalette = true;
        this.$scope.hasUniversalCanvasBackgroundImages = true;
        this.$scope.taskuuid = this.$routeParams.taskUUID;
        if (!angular.isDefined(this.$scope.taskuuid)) {
            this.$scope.taskuuid = this.TaskSelectionService.getTaskUUID();
        }
        this.$scope.objectUUID = this.$scope.contentObject.getUUID();
        this.$scope.universalCanvasURLDefault = "/iframe-tools/universal-canvas/index.html?uuid=" + this.$scope.objectUUID;
        this.$scope.universalCanvasURL = this.$scope.universalCanvasURLDefault;

        this.compressionActivate = true;
        this.compressionKeyword = "--COMPRESSION--";
        this.compressionOptions = {
            to: 'string',
            level: 9,
            gzip: true,
            header: {
                text: true
            }
        };

        this.availableTools = [
            { id: 'tool_fhpath', name: 'Pencil Tool' },
            { id: 'tool_line', name: 'Line Tool' },
            { id: "tool_path", name: "Path Tool" },
            { id: 'tool_rect', name: 'Rect Tool' },
            { id: 'tool_ellipse', name: 'Ellipse Tool' },
            { id: 'tool_circle', name: 'Circle Tool' },
            { id: 'tool_rect_radius', name: 'Rect Radius Tool' },
            { id: 'tool_diamond', name: 'Diamond Tool' },
            { id: 'tool_text', name: 'Text Tool' },
            { id: 'tool_arrow', name: 'Arrow Tool' },
        ];

        this.opacityMap = {
            '100%': 1.0,
            '90%': 0.9,
            '80%': 0.8,
            '70%': 0.7,
            '60%': 0.6,
            '50%': 0.5,
            '40%': 0.4,
            '30%': 0.3,
            '20%': 0.2,
            '10%': 0.1,
            '0%': 0.0
        };
        this.opacity = ['100%', '90%', '80%', '70%', '60%', '50%', '40%', '30%', '20%', '10%', '0%'];

        this.initOldBackgroundImageContent();
        if (this.$scope.isPDFMode || this.$scope.examExport) {
            this.$signals.emit(IB.Signals.SHEET_WAIT_REQUEST, this.$scope.contentObject.getUUID(), this.$scope.contentObject.getObjectType() + "_pdf");

            var attachement = angular.copy(this.$scope.contentObject.getAttachement());
            attachement = this.decompression(attachement);
            var pdfSVG;

            if (attachement && attachement.hasOwnProperty("svgData")) {
                this.fixSVGMediaPaths(attachement);
                pdfSVG = this.cleanSVGForPDF(attachement.svgFullData);
            } else {
                pdfSVG = this.createEmptyPDFPlaceHolderSVG();
            }
            pdfSVG = pdfSVG.replace(' href=', ' xlink:href=');
            pdfSVG = pdfSVG.replace(' xlink:xlink:href=', ' xlink:href=');
            var html$ = $(pdfSVG);
            if (attachement.backgroundImage && html$.find('#canvasBackground rect[fill="' + attachement.backgroundColor + '"]').length === 0) {
                var backgroundColor = $('<rect />');
                backgroundColor.attr('width', '100%');
                backgroundColor.attr('height', '100%');
                backgroundColor.attr('x', '0');
                backgroundColor.attr('y', '0');
                backgroundColor.attr('stroke', '#000');
                backgroundColor.attr('style', 'pointer-events:none');
                backgroundColor.attr('fill', attachement.backgroundColor);
                html$.find('#canvasBackground').append(backgroundColor);
            }
            if (attachement.backgroundImage && html$.find('#canvasBackground #background_image').length === 0) {
                var backgroundImage = $('<image />');
                backgroundImage.attr('id', 'background_image');
                backgroundImage.attr('width', '100%');
                backgroundImage.attr('height', '100%');
                backgroundImage.attr('preserveAspectRatio', 'xMinYMin');
                backgroundImage.attr('style', 'pointer-events:none');
                backgroundImage.attr('xmlns:xlink', 'http://www.w3.org/1999/xlink');
                backgroundImage.attr('data-uuid', attachement.backgroundImage);
                backgroundImage.attr('xlink:href', this.taskContext.read(attachement.backgroundImage).getExtendedMediaURL(this.$scope.taskuuid));
                html$.find('#canvasBackground').append(backgroundImage);
                pdfSVG = html$.html();
                pdfSVG = pdfSVG.replace('<img', '<image');
            }

            this.$scope.svg = this.$sce.trustAsHtml(pdfSVG);

            this.$timeout(function () {
                this.$signals.emit(IB.Signals.SHEET_WAIT_REQUEST_CLOSED, this.$scope.contentObject.getUUID(), this.$scope.contentObject.getObjectType() + "_pdf");
            }.bind(this), 2000);
        }

        this.$window.IB.iFrameDelegates[this.$scope.objectUUID] = (function (_uuid) {
            return {
                getAttachement: this.getAttachement.bind(this),
                getConfig: function () {
                    var paletteSideBarWidth = 0;
                    if (this.$scope.contentObject.getPreferences().showSideBarPalettes && this.$scope.contentObject.getPreferences().palettes.length > 0) {
                        paletteSideBarWidth = this.$scope.$parent.$parent.size == 50 ? 60 : 60;
                    }
                    return {
                        canvasHeight: parseInt(this.$scope.contentObject.getPreferences().canvasHeight),
                        canvasWidth: this.$scope.$parent.$parent.size == 50 ? 450 : (919 - paletteSideBarWidth),
                        paletteSideBarWidth: paletteSideBarWidth,
                        showGrid: this.$scope.contentObject.getPreferences().showGrid,
                        showColorOptions: this.$scope.contentObject.getPreferences().showColorOptions,
                        cols: this.$scope.$parent.$parent.size == 50 ? 2 : 1,
                        showSideBarPalettes: this.$scope.contentObject.getPreferences().showSideBarPalettes,
                        gridSnapping: this.$scope.contentObject.getPreferences().gridSnapping,
                        initFill: this.$scope.contentObject.getPreferences().initFill,
                        initStroke: this.$scope.contentObject.getPreferences().initStroke,
                        showStrokeOption: (isCMS && !isPreview) ? true : this.$scope.preferencesValues['showStrokeOption'],
                        gridMainLineAlpha: this.opacityMap[this.$scope.contentObject.getPreferences().gridMainLineAlpha],
                        gridMainLineColor: this.$scope.contentObject.getPreferences().gridMainLineColor,
                        gridSubLineAlpha: this.opacityMap[this.$scope.contentObject.getPreferences().gridSubLineAlpha],
                        gridSubLineColor: this.$scope.contentObject.getPreferences().gridSubLineColor,
                        gridMainRectDimension: this.$scope.contentObject.getPreferences().gridMainRectDimension,
                        gridSubRectNumber: this.$scope.contentObject.getPreferences().gridSubRectNumber,
                        enabledTools: _.pluck(this.$scope.contentObject.getPreferences().enabledTools, "id"),
                        enabledPalettes: _.pluck(this.$scope.contentObject.getPreferences().enabledPalettes, "id"),
                        changedMedia: this.changedMedia,
                        hideMenuBar: this.$scope.contentObject.getPreferences().hideMenuBar,
                        disableContextMenu: this.$scope.contentObject.getPreferences().disableContextMenu,
                        selectBackgroundImage: function () {
                            var imagesPalettes = _.filter(this.taskContext.findInstancesByType('universalCanvasPalette'), function (item) {
                                return item.getName() === ('__IB-BACKGROUND-IMAGES__' + this.$scope.contentObject.getUUID())
                            }.bind(this));

                            for (var key in imagesPalettes) {
                                if (imagesPalettes.hasOwnProperty(key) && imagesPalettes[key].getItems()) {
                                    if (imagesPalettes[key].getItems() && imagesPalettes[key].getItems().length > 0) {
                                        return true;
                                    }
                                }
                            }

                            return false;
                        }.bind(this),
                        selectBackgroundColor: (isCMS && !isPreview) || this.$scope.contentObject.getPreferences().selectBackgroundColor,
                        getBackgroundColor: function () {
                            var attachement = this.getAttachement();
                            if (attachement.backgroundColor) {
                                return attachement.backgroundColor;
                            }
                            return null;
                        }.bind(this),
                        getBackgroundImageUUID: function () {
                            var attachement = this.getAttachement();
                            if (attachement.backgroundImage) {
                                return attachement.backgroundImage;
                            }
                            return null;
                        }.bind(this),
                        getBackgroundImageUrl: function () {
                            var attachement = this.getAttachement();
                            if (attachement.backgroundImage) {
                                if (attachement.backgroundImage && angular.isDefined(attachement.backgroundImage)) {
                                    return this.taskContext.read(attachement.backgroundImage).getExtendedMediaURL(this.$scope.taskuuid);
                                }
                            }
                            return null;
                        }.bind(this),
                        language: function () {
                            var taskLanguage = this.TaskInfoService.getTaskInfo().getLanguageCode();
                            return this.isLanguageAvailable(taskLanguage) ? taskLanguage : 'en';
                        }.bind(this),
                        getEllipsisText: function (text, length) {
                            return this.$filter('ellipsis')(text, length);
                        }.bind(this),
                        isDeveloper: function () {
                            if (this.isPreview) {
                                return false;
                            }
                            return typeof this.$window.isCMS != 'undefined' ? this.$window.isCMS : false;
                        }.bind(this),
                        getPalettes: function () {
                            var _list = this.$scope.contentObject.getPreferences().palettes,
                                _p = [];

                            if (!angular.isDefined(_list)) {
                                return _p;
                            }
                            const taskContext = this.taskContext;
                            const taskuuid = this.$scope.taskuuid;
                            angular.forEach(_list, function (palette) {
                                if (taskContext.read(palette.id) !== undefined) {
                                    var _palette = taskContext.read(palette.id),
                                        _items = _palette.getItems();
                                    let _data = {};

                                    angular.forEach(_items, function (item, index) {
                                        var mediaObject = taskContext.read(item.uuid);
                                        if (item.uuid) {
                                            this[item.uuid] = {
                                                'comments': mediaObject.getComments(),
                                                'height': mediaObject.getHeight(),
                                                'width': mediaObject.getWidth(),
                                                'type': 'image',
                                                'xlink': mediaObject.getExtendedMediaURL(taskuuid)
                                            };
                                        }
                                    }, _data);

                                    this.push({
                                        uuid: _palette.getUUID(),
                                        name: _palette.getName(),
                                        items: _data
                                    });
                                }

                            }, _p);

                            if (this.$scope.examExport) {
                                this.$scope.palettes = _p;
                            }

                            return _p;
                        }.bind(this),
                        getBackgroundImages: function () {
                            var instances = this.taskContext.findInstancesByType('universalCanvasPalette');

                            var defaultImagePalettes = _.filter(instances, function (item) {
                                return item.getName() === ('__IB-BACKGROUND-IMAGE-DEFAULT__' + this.$scope.contentObject.getUUID())
                            }.bind(this));

                            var imagesPalettes = _.filter(instances, function (item) {
                                return item.getName() === ('__IB-BACKGROUND-IMAGES__' + this.$scope.contentObject.getUUID())
                            }.bind(this));
                            var result = {
                                defaultImage: [],
                                images: []
                            };

                            for (var key in defaultImagePalettes) {
                                if (defaultImagePalettes.hasOwnProperty(key) && defaultImagePalettes[key].getItems()) {
                                    result.defaultImage = result.defaultImage.concat(defaultImagePalettes[key].getItems().map(function (item) {
                                        return {
                                            'uuid': item.uuid,
                                            'type': 'image',
                                            'xlink': this.taskContext.read(item.uuid).getExtendedMediaURL(this.$scope.taskuuid)
                                        };
                                    }.bind(this)));
                                }
                            }

                            for (var key in imagesPalettes) {
                                if (imagesPalettes.hasOwnProperty(key) && imagesPalettes[key].getItems()) {
                                    result.images = result.images.concat(imagesPalettes[key].getItems().map(function (item) {
                                        return {
                                            'uuid': item.uuid,
                                            'type': 'image',
                                            'position': item.position,
                                            'xlink': this.taskContext.read(item.uuid).getExtendedMediaURL(this.$scope.taskuuid)
                                        };
                                    }.bind(this)));
                                    result.images = _.sortBy(result.images, 'position');
                                }
                            }
                            return result;
                        }.bind(this),
                        mediaContent: this.$scope.contentObject.getPreferences().mediaContent,
                        mediaContentUrl: function () {
                            var tmp = this.$scope.contentObject.getPreferences().mediaContent;
                            if (typeof tmp != 'undefined' && tmp) {
                                tmp = this.taskContext.read(this.$scope.contentObject.getPreferences().mediaContent).getExtendedMediaURL(this.$scope.taskuuid);
                            }
                            return tmp;
                        }.bind(this)
                    };
                }.bind(this),
                fireUpdate: function (attachement) {
                    this.$scope.contentObject.setAttachement(this.compression(attachement));

                    if (!this.isCMSNotPreview) {
                        this.advanceQuestionStatusIfContentChanged();
                        this.$scope.contentObject.save();
                    }
                }.bind(this),
                firedUCDimension: function (dimension) {
                    const iFrameStyle = this.$scope.iFrameStyle;
                    const currentDimension = {
                        width: parseFloat(iFrameStyle.width.replace(new RegExp('px', 'g'), '')),
                        height: parseFloat(iFrameStyle.height.replace(new RegExp('px', 'g'), ''))
                    };
                    if (currentDimension.height < dimension.height) {
                        var dH = dimension.height - currentDimension.height;
                        this.$timeout(function () {
                            iFrameStyle.height = (currentDimension.height + dH) + 'px';
                        }.bind(this));
                    }
                }.bind(this),
                setBackgroundColor: function (color) {
                    var attachement = this.getAttachement();
                    attachement.backgroundColor = color;
                    this.$scope.contentObject.setAttachement(this.compression(attachement));
                    this.$scope.contentObject.flagAsDirty();
                    if (!this.isCMSNotPreview) {
                        console.log("CCCCC");
                        this.advanceQuestionStatusIfContentChanged();
                    } else {
                        console.log("!CCCC");
                    }
                }.bind(this),
                setBackgroundImageUUID: function (uuid) {
                    var attachement = this.getAttachement();
                    attachement.backgroundImage = uuid;
                    this.$scope.contentObject.setAttachement(this.compression(attachement));
                    this.$scope.contentObject.flagAsDirty();
                    if (!this.isCMSNotPreview) {
                        this.advanceQuestionStatusIfContentChanged();
                    }
                }.bind(this),
                historyObject: function () {
                    return this.UniversalCanvasService.getHistoryObject(_uuid)
                }.bind(this),
                cursorFocus: function (elem) {
                    var x = window.scrollX;
                    var y = window.scrollY;
                    elem.focus();
                    window.scrollTo(x, y);
                }.bind(this)
            };
        }.bind(this))(this.$scope.objectUUID);

        this.$scope.resetChanges = () => {
            console.log("override # <" + this.injections.$scope.contentObject.getUUID() + "> resetChanges()");
            this.$scope.resetChanges();
            this.initPreferences();  // the "reset"
            this.$scope.$broadcast('reset_multiselect');
        };


        var getMediaList = function () {
            var objs = this.taskContext.findInstancesByType('media'),
                objList = Array();

            for (var key in objs) {
                objList.push({
                    uuid: key,
                    name: key + '.' + objs[key].getFileExtension(),
                    originalname: objs[key].getUploadName(),
                    size: angular.bytesToSize(objs[key].getFileSize()),
                    bytes: objs[key].getFileSize(),
                    type: objs[key].getFileType(),
                    mimetype: objs[key].getMimeType(),
                    thumbnail: objs[key].getThumbnailURL(this.$scope.taskuuid) + '?' + Math.floor((Math.random() * 1000000000001)),
                    comments: objs[key].getComments(),
                    height: objs[key].getHeight(),
                    width: objs[key].getWidth()
                });
            }
            return objList;
        }.bind(this);
        // window.mediaList = getMediaList();

        var modal = {
            open: function ($uibModal, templateUrl, settings) {
                this.modalInstance = $uibModal.open({
                    templateUrl: '/views/ib/cms/eassessment/modal/' + templateUrl,
                    // scope: $scope,
                    windowClass: 'universal-canvas-palette',
                    resolve: {
                        'bridges': function () {
                            return this.$bridges;
                        }.bind(this),
                        'taskContext': function () {
                            return this.taskContext;
                        }.bind(this),
                        'contentObjectUUID': function () {
                            return this.$scope.contentObject.getUUID();
                        }.bind(this),
                        'taskuuid': function () {
                            return this.$scope.taskuuid;
                        }.bind(this),
                        'mediaList': function () {
                            return getMediaList();
                        }.bind(this),
                        'currentmediaUUID': function () {
                            return this.$scope.contentObject.getPreference('mediaContent');
                        }.bind(this),
                        'defaultPaletteName': function () {
                            return defaultPaletteName;
                        }.bind(this),
                        'settings': function () {
                            return settings;
                        }.bind(this)
                    },
                    controller: 'UniversalCanvasMediaBrowserModalController'
                });
                this.modalInstance.result.then(
                    function (data) {
                        if (settings && settings.backgroundPalette) {
                            var instances = this.taskContext.findInstancesByType('universalCanvasPalette');
                            var attachement = this.getAttachement();

                            var defaultImagePalettes = _.filter(instances, function (item) {
                                return item.getName() === ('__IB-BACKGROUND-IMAGE-DEFAULT__' + this.$scope.contentObject.getUUID())
                            }.bind(this));
                            if (defaultImagePalettes && defaultImagePalettes[0] && defaultImagePalettes[0].getItems() && defaultImagePalettes[0].getItems().length > 0) {
                                var items = defaultImagePalettes[0].getItems();
                                attachement.backgroundImage = items[0].uuid;
                                this.$scope.contentObject.setAttachement(this.compression(attachement));
                            } else {
                                attachement.backgroundImage = null;
                                this.$scope.contentObject.setAttachement(this.compression(attachement));
                            }
                            this.$scope.contentObject.flagAsDirty();

                        }
                        if (data && data.reloadCanvas === true) {
                            this.reloadCanvas();
                        }

                    }.bind(this),
                    function () {
                    }
                );
            }.bind(this),
            close: function () {
                if (this.modalInstance !== null) {
                    this.modalInstance.close();
                    this.modalInstance = undefined;
                }
            }.bind(this)
        };

        // -------------------------------------------
        // Modal for media selection
        // var modalInstance = null;
        this.$scope.onMediaBrowseButtonClick = function () {
            var templateUrl = 'eassessmentmediabrowserModal.html';
            modal.open(this.$uibModal, templateUrl);
        }.bind(this);

        var focusInterval;
        var isFocus = false;
        var isMouseOver = false;


        var keydownHandler = function (event) {
            if ((event.altKey && event.keyCode === 32) || (event.key === " " && event.getModifierState("Alt"))) {
                event.preventDefault();
                event.stopPropagation();
            }
        };

        this.$scope.$parent.element.on('mouseover', function () {
            this.FreezeService.initFreezeTimeout(1000);
            isMouseOver = true;
            if (focusInterval) return false;
            focusInterval = this.$interval(function () {
                isFocus = document.activeElement === this.$scope.$parent.element.find('iframe')[0];
                if (isFocus) {
                    this.$scope.$parent.element.find('iframe')[0].contentDocument.body.addEventListener('keydown', keydownHandler)
                } else {
                    if (!isMouseOver && focusInterval) {
                        this.$interval.cancel(focusInterval);
                        focusInterval = false;
                    }
                }
            }.bind(this), 1000, 0, false);
        }.bind(this));

        this.$scope.$parent.element.on('mouseout', function () {
            isMouseOver = false;
            isFocus = document.activeElement === this.$scope.$parent.element.find('iframe')[0];
            this.FreezeService.initFreezeTimeout();
            this.$scope.$parent.element.find('iframe')[0].contentDocument.body.removeEventListener('keydown', keydownHandler);
        }.bind(this));

        this.$scope.onUniversalCanvasPaletteButtonClick = function () {
            var templateUrl = 'universalcanvaspaletteModal.html';
            this.modal.open(this.$uibModal, templateUrl);
        }.bind(this);

        this.$scope.onUniversalCanvasBackgroundImagesButtonClick = function () {
            var templateUrl = 'universalcanvasBackgroundPaletteModal.html';
            this.modal.open(this.$uibModal, templateUrl, {
                backgroundPalette: true
            });
        }.bind(this);

        // When we get destroyed, we need to unbind the signal
        this.$scope.$on('$destroy', function () {
            if (this.$window.IB.iFrameDelegates[this.$scope.objectUUID] && angular.isFunction(this.$window.IB.iFrameDelegates[this.$scope.objectUUID].saveChanges)) {
                this.$window.IB.iFrameDelegates[this.$scope.objectUUID].saveChanges();
            }
            this.$signals.remove(IB.Signals.CMS_MEDIA_OBJECT_SELECTED, this.onSelectMedia);
            this.$signals.remove(IB.Signals.BEFORE_SHEET_BLUR, this.onBeforeSheetBlur);

            if (focusInterval) {
                this.$interval.cancel(focusInterval);
            }
            this.UniversalCanvasService.destroyHistoryObject(this.$scope.objectUUID);

            $('iframe').contents().find('html').off('click', this.clickOutsideForIframes);
            $('iframe').contents().find('#svgroot').off('click', this.clickOutsideForIframes);
            $(document).off('click', this.clickOutside);
        }.bind(this));

        // Bind to media select/deselect signal (triggered in media browser modal window)
        this.$signals.on(IB.Signals.CMS_MEDIA_OBJECT_SELECTED, this.onSelectMedia);

        // Trigger universal canvas save action onBeforeSheetBlur
        this.$signals.on(IB.Signals.BEFORE_SHEET_BLUR, this.onBeforeSheetBlur);

        //initializing object
        this.initPreferences();
        this.iframestyle();

        var container = $('[container-ref="' + this.$scope.objectUUID + '"]');
        this.clickOutside = function (event) {
            if (!container.is(event.target) && container.has(event.target).length === 0 && this.$window.IB.iFrameDelegates[this.$scope.objectUUID] && this.$window.IB.iFrameDelegates[this.$scope.objectUUID].unSelect) {
                this.$window.IB.iFrameDelegates[this.$scope.objectUUID].unSelect();
            }
        }.bind(this);

        this.clickOutsideForIframes = function (event) {
            if ($(event.target).closest('body[uuid="' + this.$scope.objectUUID + '"]').length === 0 && this.$window.IB.iFrameDelegates[this.$scope.objectUUID] && this.$window.IB.iFrameDelegates[this.$scope.objectUUID].unSelect) {
                this.$window.IB.iFrameDelegates[this.$scope.objectUUID].unSelect();
            }
        }.bind(this);

        this.SheetLoadedService
            .getPromise()
            .then(function () {
                $('iframe').contents().find('html').on('click', this.clickOutsideForIframes);
                $('iframe').contents().find('#svgroot').on('click', this.clickOutsideForIframes);
                $(document).on('click', this.clickOutside);
            }.bind(this));
    }

    /**
     * Gets and processes the attachment data for the Universal Canvas.
     * @returns {Object} The processed attachment object.
     */
    getAttachement() {
        let attachement = angular.copy(this.$scope.contentObject.getAttachement());
        attachement = this.decompression(attachement);
        attachement = !attachement ? {} : attachement;

        if (typeof (attachement.svgData) !== 'undefined') {
            attachement.svgData = this.addUCFontsToSVG(attachement.svgData);
        }
        if (typeof (attachement.svgFullData) !== 'undefined') {
            attachement.svgFullData = this.addUCFontsToSVG(attachement.svgFullData);
        }

        if (this.isPreview) {
            return this.fixSVGMediaPaths(angular.copy(attachement));
        }

        if (!this.isCMSNotPreview) {
            if (typeof (this.$scope.contentObject.getAttachement()) === 'undefined') {
                this.$scope.contentObject.setAttachement(this.compression(attachement));
            }
        }

        if (typeof (attachement.svgData) !== 'undefined') {
            attachement.svgData = attachement.svgData.replace(/>\s+<|&nbsp;/g, function (match) {
                return match === "&nbsp;" ? "" : "><";
            });
        }
        if (typeof (attachement.svgFullData) !== 'undefined') {
            attachement.svgFullData = attachement.svgFullData.replace(/>\s+<|&nbsp;/g, function (match) {
                return match === "&nbsp;" ? "" : "><";
            });
        }

        this.fixSVGMediaPaths(attachement);
        return attachement;
    }

    /**
     * Compare the pre-populated SVG against the current candidate-created SVG
     * and if it differs, advance the question status.
     * @returns {boolean} True if the content has changed, false otherwise.
     */
    advanceQuestionStatusIfContentChanged() {
        const currentStatus = this.getQuestionPartStatus();

        if (currentStatus !== 0) {
            return false;
        }

        let isChanged = false;
        const oldSVG = !angular.isUndefined(this.preAttachement.svgData)
            ? this.normaliseSVG(this.preAttachement.svgData)
            : false;

        if (!oldSVG) {
            console.warn("UC: skipping advanceQuestionStatusIfContentChanged, no preAttachement SVG data");
            return false;
        }

        if (typeof (this.$scope.contentObject.getAttachement()) === 'undefined') {
            console.warn("UC: skipping advanceQuestionStatusIfContentChanged, no attachement data");
            return false;
        }

        let attachement = angular.copy(this.$scope.contentObject.getAttachement());
        attachement = this.decompression(attachement);

        const newSVG = this.normaliseSVG(attachement.svgData);
        if (oldSVG !== newSVG) {
            this.advanceQuestionPartStatus();
            isChanged = true;
        }

        return isChanged;
    }

    /**
     * Normalizes SVG content by sorting attributes and removing whitespace
     * to make SVG comparison more reliable.
     * @param {string} svg - The SVG content to normalize.
     * @returns {string} The normalized SVG content.
     */
    normaliseSVG(svg) {
        return this.UniversalCanvasService.normaliseSVG(svg, this.$, this.isCMSNotPreview);
    }
}

/**
 * Registers the UniversalCanvasObject directive and content object.
 * @param {Object} module - The Angular module to register with.
 */
export default function registerUniversalCanvasObject(module) {
    module.directive('asfUniversalcanvasObject', function () {
        return {
            restrict: 'EA',
            replace: true,
            scope: {
                "examExport": '=?',
                "contentObject": "="
            },
            templateUrl: "/views/ib/contentobjects/universalcanvas.html",
            controller: AsfUniversalCanvasObjectCtrl
        };
    });

    module.run(['ContentObjectRegistryService', function (ContentObjectRegistryService) {
        var obj = [{
            "uuid": "",
            "content": "",
            "objectType": "universalcanvas",
            "type": "contentObject",
            "contentReference": false,
            "preferences": {
                "mediaContent": undefined,
                "canvasHeight": 400,
                "showGrid": false,
                "showColorOptions": true,
                "showSideBarPalettes": true,
                "gridSnapping": false,
                "gridMainLineAlpha": 0.5,
                "gridMainLineColor": '#000000',
                "gridSubLineAlpha": 0.2,
                "gridSubLineColor": '#000000',
                "gridMainRectDimension": 100,
                "gridSubRectNumber": 10,
                "initFill": "#ffffff",
                "initStroke": "#000000",
                "showStrokeOption": true,
                "enabledTools": [],
                "includedInPDF": true,
                "hideMenuBar": false,
                "disableContextMenu": false,
                "palettes": [],
                "selectBackgroundColor": false
            }
        }];
        var objProperties = {
            type: "universalcanvas",
            label: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.UNIVERSAL-CANVAS.LABEL',
            cssClass: "new-universal-canvas-object",
            icon: "cms/icon-universal-canvas.png",
            description: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.UNIVERSAL-CANVAS.DESCRIPTION',
            group: {
                id: 3
            }
        };
        ContentObjectRegistryService.register(obj, objProperties);
    }]);
}